import axios from 'axios';

const COINGECKO_BASE_URL = 'https://api.coingecko.com/api/v3';
const DEFILLAMA_BASE_URL = 'https://yields.llama.fi';

// Cache implementation
const cache = new Map();
const CACHE_DURATION = 30000; // 30 seconds

function getCached(key) {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
}

function setCached(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

export async function fetchTokenPrice(tokenId = 'ethereum') {
  const cacheKey = `price-${tokenId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await axios.get(
      `${COINGECKO_BASE_URL}/simple/price?ids=${tokenId}&vs_currencies=usd&include_24hr_change=true`
    );
    
    if (!response.data[tokenId]) {
      throw new Error(`No price data for ${tokenId}`);
    }
    
    const data = response.data[tokenId];
    setCached(cacheKey, data);
    return data;
  } catch (error) {
    console.error('Error fetching token price:', error);
    throw new Error(`Failed to fetch ${tokenId} price: ${error.message}`);
  }
}

export async function fetchTokenMarketData(tokenId = 'ethereum') {
  const cacheKey = `market-${tokenId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await axios.get(
      `${COINGECKO_BASE_URL}/coins/${tokenId}?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false`
    );
    
    const data = response.data.market_data;
    setCached(cacheKey, data);
    return data;
  } catch (error) {
    console.error('Error fetching token market data:', error);
    throw new Error(`Failed to fetch ${tokenId} market data: ${error.message}`);
  }
}

export async function fetchTokenVolume(tokenId = 'ethereum') {
  try {
    const marketData = await fetchTokenMarketData(tokenId);
    return marketData.total_volume.usd;
  } catch (error) {
    console.error('Error fetching token volume:', error);
    throw error;
  }
}

export async function fetchProtocolApy(tokenId = 'ethereum') {
  const cacheKey = `apy-${tokenId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const response = await axios.get(`${DEFILLAMA_BASE_URL}/pools`);
    const pools = response.data.data;
    
    // Filter pools by chain and calculate average APY
    const chainMapping = {
      'ethereum': 'Ethereum',
      'solana': 'Solana',
      'cardano': 'Cardano',
      'polkadot': 'Polkadot',
      'binancecoin': 'BSC'
    };
    
    const chainName = chainMapping[tokenId];
    if (chainName) {
      const chainPools = pools.filter(pool => 
        pool.chain === chainName && pool.apy && pool.apy > 0
      );
      
      if (chainPools.length > 0) {
        const totalApy = chainPools.reduce((sum, pool) => sum + pool.apy, 0);
        const avgApy = totalApy / chainPools.length;
        setCached(cacheKey, avgApy);
        return avgApy;
      }
    }
    
    throw new Error(`No APY data available for ${tokenId}`);
  } catch (error) {
    console.error('Error fetching APY data:', error);
    throw new Error(`Failed to fetch APY for ${tokenId}: ${error.message}`);
  }
}

export async function fetchTotalValueLocked(tokenId = 'ethereum') {
  const cacheKey = `tvl-${tokenId}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;

  try {
    const chainMapping = {
      'ethereum': 'Ethereum',
      'binancecoin': 'BSC',
      'solana': 'Solana',
      'polkadot': 'Polkadot',
      'cardano': 'Cardano'
    };

    const chainName = chainMapping[tokenId];
    
    if (!chainName) {
      throw new Error(`No TVL data for ${tokenId}`);
    }

    const response = await axios.get(`https://api.llama.fi/v2/historicalChainTvl/${chainName}`);
    const tvlData = response.data;
    const currentTvl = tvlData[tvlData.length - 1]?.tvl;
    
    if (!currentTvl) {
      throw new Error(`No current TVL data for ${chainName}`);
    }
    
    setCached(cacheKey, currentTvl);
    return currentTvl;
  } catch (error) {
    console.error('Error fetching TVL:', error);
    throw new Error(`Failed to fetch TVL for ${tokenId}: ${error.message}`);
  }
}